/**
 * Groups Service
 *
 * DB-first data layer for the groups system. All reads/writes go to Supabase
 * `groups` and `connection_groups` tables. The DB is the source of truth; the
 * hook layer (useGroups) maintains a localStorage read cache (`groups_data_cache`)
 * per data-and-state-guide.md § DB-First Architecture.
 *
 * Read functions THROW on Supabase error (cached-read contract: a failed fetch
 * must never look like an empty result, or it would overwrite good cached data).
 *
 * This is permanent code — designed to port directly to React Native.
 */

import { supabase } from '../supabase';
import { SOURCE_BRANCH } from '../../utils/deployInfo';

/**
 * Fetch all groups for a user, ordered by sort_order.
 * Returns preset groups first, then custom groups. Throws on Supabase error.
 * @param {string} profileUserId
 * @returns {Promise<Array>} Array of group objects
 */
export const fetchUserGroups = async (profileUserId) => {
  if (!profileUserId) return [];

  const { data, error } = await supabase
    .from('groups')
    .select('id, name, is_preset, parent_group_id, sort_order')
    .eq('profile_user_id', profileUserId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(`[GROUPS] fetchUserGroups failed: ${error.message}`);
  }

  return data || [];
};

/**
 * Fetch groups assigned to a single connection.
 * @param {string} connectionViewId - UUID of the connection_views row
 * @returns {Promise<Array>} Array of group objects
 */
export const fetchConnectionGroups = async (connectionViewId) => {
  if (!connectionViewId) return [];

  const { data, error } = await supabase
    .from('connection_groups')
    .select('group_id, groups(id, name, is_preset, parent_group_id, sort_order)')
    .eq('connection_view_id', connectionViewId);

  if (error) {
    console.error('[GROUPS] fetchConnectionGroups failed:', error.message);
    return [];
  }

  return (data || []).map((row) => row.groups).filter(Boolean);
};

/**
 * Fetch all connection-group assignments for a user.
 * Returns a map: connectionViewId -> array of group objects.
 * Accepts pre-fetched views to avoid a duplicate connection_views query.
 * Throws on Supabase error.
 * @param {Array} views - Array of { id, chat_room_id } from fetchConnectionViews
 * @returns {Promise<Object>} Map of connectionViewId -> group objects array
 */
export const fetchAllConnectionGroups = async (views) => {
  if (!views || views.length === 0) return {};

  const viewIds = views.map((v) => v.id);

  const { data: assignments, error: assignError } = await supabase
    .from('connection_groups')
    .select(
      'connection_view_id, group_id, groups(id, name, is_preset, parent_group_id, sort_order)'
    )
    .in('connection_view_id', viewIds);

  if (assignError) {
    throw new Error(`[GROUPS] fetchAllConnectionGroups failed: ${assignError.message}`);
  }

  // Build map: connectionViewId -> groups
  const map = {};
  for (const row of assignments || []) {
    if (!row.groups) continue;
    if (!map[row.connection_view_id]) {
      map[row.connection_view_id] = [];
    }
    map[row.connection_view_id].push(row.groups);
  }

  return map;
};

/**
 * Replace all group assignments for a connection.
 * Enforces Close Friends subset rule: Close Friends implies Friends.
 * @param {string} connectionViewId - UUID of the connection_views row
 * @param {string[]} groupIds - Array of group UUIDs to assign
 * @param {Array} allGroups - User's full groups list (for subset enforcement)
 * @returns {Promise<boolean>} Success
 */
export const setConnectionGroups = async (connectionViewId, groupIds, allGroups) => {
  if (!connectionViewId) return false;

  // A group with a parent implies its parent (Close Friends → Friends). Keyed on
  // parent_group_id, NOT the name, so a rename keeps the coupling (detail-pass-3 §8b).
  const finalGroupIds = [...groupIds];
  for (const g of allGroups) {
    if (
      g.parent_group_id &&
      finalGroupIds.includes(g.id) &&
      !finalGroupIds.includes(g.parent_group_id)
    ) {
      finalGroupIds.push(g.parent_group_id);
    }
  }

  // Delete all existing assignments, then insert new ones
  const { error: deleteError } = await supabase
    .from('connection_groups')
    .delete()
    .eq('connection_view_id', connectionViewId);

  if (deleteError) {
    console.error('[GROUPS] setConnectionGroups delete failed:', deleteError.message);
    return false;
  }

  if (finalGroupIds.length === 0) return true;

  const rows = finalGroupIds.map((groupId) => ({
    connection_view_id: connectionViewId,
    group_id: groupId,
    source_branch: SOURCE_BRANCH,
  }));

  const { error: insertError } = await supabase.from('connection_groups').insert(rows);

  if (insertError) {
    console.error('[GROUPS] setConnectionGroups insert failed:', insertError.message);
    return false;
  }

  return true;
};

/**
 * Create a custom group for a user.
 * @param {string} profileUserId
 * @param {string} name - Group name
 * @returns {Promise<Object|null>} Created group object or null on failure
 */
export const createCustomGroup = async (profileUserId, name, sortOrder = 100) => {
  if (!profileUserId || !name?.trim()) return null;

  const { data, error } = await supabase
    .from('groups')
    .insert({
      profile_user_id: profileUserId,
      name: name.trim(),
      is_preset: false,
      // Incrementing so new groups keep their own place in the reorder / chip order
      // (detail-pass-3 §8b); the hook passes max(existing sort_order) + 1.
      sort_order: sortOrder,
      source_branch: SOURCE_BRANCH,
    })
    .select('id, name, is_preset, parent_group_id, sort_order')
    .single();

  if (error) {
    console.error('[GROUPS] createCustomGroup failed:', error.message);
    return null;
  }

  return data;
};

/**
 * Delete a custom group. Cascade removes connection_groups rows.
 * Preset groups cannot be deleted.
 * @param {string} groupId - UUID of the group to delete
 * @returns {Promise<boolean>} Success
 */
export const deleteCustomGroup = async (groupId) => {
  if (!groupId) return false;

  const { error } = await supabase.from('groups').delete().eq('id', groupId).eq('is_preset', false); // Safety: never delete presets

  if (error) {
    console.error('[GROUPS] deleteCustomGroup failed:', error.message);
    return false;
  }

  return true;
};

/**
 * Rename any group — preset or custom (detail-pass-3 §8b). The coupling is keyed
 * on parent_group_id, so a rename never breaks Close Friends ⇒ Friends.
 */
export const renameGroup = async (groupId, name) => {
  if (!groupId || !name?.trim()) return false;
  const { error } = await supabase.from('groups').update({ name: name.trim() }).eq('id', groupId);
  if (error) {
    console.error('[GROUPS] renameGroup failed:', error.message);
    return false;
  }
  return true;
};

/**
 * Reorder groups — write each id's new sort_order (its index). This order is the
 * order of the People screen's chips (detail-pass-3 §8b). Per-row writes: a partial
 * failure only leaves the order slightly off and is re-doable (no referential
 * invariant at stake, unlike the parent FK).
 */
export const reorderGroups = async (orderedIds) => {
  if (!orderedIds?.length) return false;
  const results = await Promise.all(
    orderedIds.map((id, i) => supabase.from('groups').update({ sort_order: i }).eq('id', id))
  );
  const failed = results.find((r) => r.error);
  if (failed) {
    console.error('[GROUPS] reorderGroups failed:', failed.error.message);
    return false;
  }
  return true;
};

/**
 * Delete any group — preset or custom (detail-pass-3 §8b). `connection_groups`
 * rows cascade; the `parent_group_id` FK is ON DELETE SET NULL (migration 045), so
 * deleting Friends leaves Close Friends standing alone, atomically.
 */
export const deleteGroup = async (groupId) => {
  if (!groupId) return false;
  const { error } = await supabase.from('groups').delete().eq('id', groupId);
  if (error) {
    console.error('[GROUPS] deleteGroup failed:', error.message);
    return false;
  }
  return true;
};

/**
 * Fetch connection_views for a user. Used to build chatRoomId -> connectionViewId lookup.
 * Throws on Supabase error.
 * @param {string} profileUserId
 * @returns {Promise<Array>} Array of { id, chat_room_id }
 */
export const fetchConnectionViews = async (profileUserId) => {
  if (!profileUserId) return [];

  const { data, error } = await supabase
    .from('connection_views')
    .select('id, chat_room_id')
    .eq('user_id', profileUserId);

  if (error) {
    throw new Error(`[GROUPS] fetchConnectionViews failed: ${error.message}`);
  }

  return data || [];
};

/**
 * Fetch the full groups bundle for a user in one call — the shape cached by
 * useGroups under `groups_data_cache`. Throws on any underlying failure so a
 * failed refresh never overwrites the cache with an empty bundle.
 * @param {string} profileUserId
 * @returns {Promise<{groups: Array, views: Array, connectionGroups: Object}>}
 */
export const fetchGroupsBundle = async (profileUserId) => {
  if (!profileUserId) return { groups: [], views: [], connectionGroups: {} };

  const [groups, views] = await Promise.all([
    fetchUserGroups(profileUserId),
    fetchConnectionViews(profileUserId),
  ]);
  const connectionGroups = await fetchAllConnectionGroups(views);

  return { groups, views, connectionGroups };
};
